import { useState, useMemo } from "react";
import { DAYS, TIMES, STATUS, STATUS_ORDER } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Info } from "lucide-react";
import ExampleGrid from "@/components/ExampleGrid";

/**
 * Interactive availability grid.
 * Props:
 *  - value: { "lun_14:00": "green" | "yellow" | "red", ... }
 *  - onChange(nextValue)
 */
export default function AvailabilityGrid({ value, onChange }) {
  const [activeStatus, setActiveStatus] = useState("green");

  const setSlot = (day, time) => {
    const key = `${day}_${time}`;
    const current = value[key];
    const next = { ...value };
    // If clicking with same active status, toggle off. Else set to active.
    if (current === activeStatus) {
      delete next[key];
    } else {
      next[key] = activeStatus;
    }
    onChange(next);
  };

  const counts = useMemo(() => {
    const c = { green: 0, yellow: 0, red: 0 };
    Object.values(value).forEach((s) => c[s] !== undefined && c[s]++);
    return c;
  }, [value]);

  return (
    <div className="space-y-5">
      {/* Example (collapsible) */}
      <ExampleGrid />

      {/* Legend & Tool Picker */}
      <div className="rounded-xl bg-white border border-slate-200 p-4 card-warm">
        <div className="flex items-start gap-2 mb-3">
          <Info className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
          <p className="text-sm text-slate-600 leading-relaxed">
            Seleziona prima un <strong>colore</strong> nei tre pulsanti qui sotto, poi tocca le fasce orarie di 30 minuti
            <span className="text-slate-500"> (l&apos;orario indicato è l&apos;inizio della fascia: es. <strong>15:00</strong> = 15:00–15:30)</span>.
            Ritocca lo stesso slot per rimuoverlo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="color-picker">
          {STATUS_ORDER.map((k) => {
            const s = STATUS[k];
            const active = activeStatus === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setActiveStatus(k)}
                data-testid={`color-tool-${k}`}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full px-4 py-2 border transition-all",
                  active
                    ? `${s.bg} text-white border-transparent shadow-md scale-[1.02]`
                    : `bg-white ${s.text} ${s.border} hover:shadow-sm`
                )}
              >
                <span
                  className={cn(
                    "w-3 h-3 rounded-full",
                    active ? "bg-white/90" : s.bg
                  )}
                />
                <span className="text-sm font-semibold">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:block rounded-2xl bg-white border border-slate-200 p-5 card-warm overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-[80px_repeat(5,minmax(0,1fr))] gap-2 mb-3">
            <div />
            {DAYS.map((d) => (
              <div key={d.key} className="flex flex-col items-center gap-1 px-1">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  {d.label}
                </span>
              </div>
            ))}
          </div>

          {TIMES.map((t) => (
            <div
              key={t}
              className="grid grid-cols-[80px_repeat(5,minmax(0,1fr))] gap-2 mb-1.5 items-center"
            >
              <div className="text-xs font-mono font-semibold text-slate-500 pr-2 text-right">
                {t}
              </div>
              {DAYS.map((d) => {
                const key = `${d.key}_${t}`;
                const status = value[key];
                const s = status ? STATUS[status] : null;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSlot(d.key, t)}
                    data-testid={`slot-${d.key}-${t}`}
                    className={cn(
                      "slot-btn h-10 rounded-lg border text-xs font-medium",
                      s
                        ? `${s.bg} text-white border-transparent shadow-sm slot-pop`
                        : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100"
                    )}
                  >
                    {s ? s.label.split(" ")[0] : "—"}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile tabs view */}
      <div className="md:hidden">
        <Tabs defaultValue="lun" className="w-full">
          <TabsList className="grid grid-cols-5 w-full h-auto">
            {DAYS.map((d) => (
              <TabsTrigger
                key={d.key}
                value={d.key}
                data-testid={`day-tab-${d.key}`}
                className="text-xs py-2"
              >
                {d.short}
              </TabsTrigger>
            ))}
          </TabsList>
          {DAYS.map((d) => (
            <TabsContent key={d.key} value={d.key} className="mt-4">
              <div className="rounded-2xl bg-white border border-slate-200 p-4 card-warm">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h4 className="font-bold text-slate-900">{d.label}</h4>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {TIMES.map((t) => {
                    const key = `${d.key}_${t}`;
                    const status = value[key];
                    const s = status ? STATUS[status] : null;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSlot(d.key, t)}
                        data-testid={`slot-mobile-${d.key}-${t}`}
                        className={cn(
                          "slot-btn h-12 rounded-lg border flex items-center justify-between px-3",
                          s
                            ? `${s.bg} text-white border-transparent shadow-sm slot-pop`
                            : "bg-slate-50 border-slate-200 text-slate-500"
                        )}
                      >
                        <span className="font-mono text-xs font-semibold">{t}</span>
                        <span className="text-xs font-medium">
                          {s ? s.label.split(" ")[0] : "—"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Counts */}
      <div className="flex flex-wrap gap-2 text-xs" data-testid="availability-counts">
        {STATUS_ORDER.map((k) => (
          <div
            key={k}
            className={cn(
              "inline-flex items-center gap-2 rounded-full px-3 py-1.5 border",
              STATUS[k].border,
              STATUS[k].bgLight,
              STATUS[k].text
            )}
          >
            <span className={cn("w-2 h-2 rounded-full", STATUS[k].bg)} />
            <span className="font-semibold">{STATUS[k].label}:</span>
            <span className="font-mono">{counts[k]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
